import { type FC, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
	findGoalOption,
	getDefaultGoalForObjective,
	getObjectiveRules,
	TIKTOK_ADS_AGE_GROUPS,
	type TikTokAdsAgeGroup,
	TikTokAdsBudgetMode,
	TikTokAdsGender,
	TikTokAdsObjective,
	type TikTokAdsOptimizationGoal,
	TikTokAdsPlacementType,
	TikTokAdsProductType,
} from "shared";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import {
	inputValueToSchedule,
	scheduleToInputValue,
	useTikTokCampaignWizard,
} from "./TikTokCampaignWizardContext";
import { useTikTokAdsRegions } from "./useTikTokCampaigns";

const AGE_LABELS: Record<TikTokAdsAgeGroup, string> = {
	AGE_13_17: "13–17",
	AGE_18_24: "18–24",
	AGE_25_34: "25–34",
	AGE_35_44: "35–44",
	AGE_45_54: "45–54",
	AGE_55_100: "55+",
};

const PLACEMENT_OPTIONS = [
	{ value: "PLACEMENT_TIKTOK", label: "TikTok" },
	{ value: "PLACEMENT_PANGLE", label: "Pangle" },
	{ value: "PLACEMENT_GLOBAL_APP_BUNDLE", label: "Global App Bundle" },
];

export const StepTikTokAdGroup: FC = () => {
	const { draft, updateDraft, nextStep, prevStep } = useTikTokCampaignWizard();
	const [error, setError] = useState("");
	const { data: regions, isLoading: regionsLoading } = useTikTokAdsRegions();

	const goalOptions = getObjectiveRules(draft.objective).goals;
	const selectedGoal =
		findGoalOption(draft.objective, draft.optimizationGoal) ??
		getDefaultGoalForObjective(draft.objective);

	const toggleAgeGroup = (group: TikTokAdsAgeGroup) => {
		updateDraft({
			ageGroups: draft.ageGroups.includes(group)
				? draft.ageGroups.filter((g) => g !== group)
				: [...draft.ageGroups, group],
		});
	};

	const togglePlacement = (placement: string) => {
		updateDraft({
			placements: draft.placements.includes(placement)
				? draft.placements.filter((p) => p !== placement)
				: [...draft.placements, placement],
		});
	};

	const handleNext = () => {
		if (draft.locationIds.length === 0) {
			setError("Select at least one target location.");
			return;
		}
		if (draft.ageGroups.length === 0) {
			setError("Select at least one age group.");
			return;
		}
		if (
			draft.budgetMode === TikTokAdsBudgetMode.LIFETIME &&
			!draft.scheduleEnd
		) {
			setError("A lifetime budget needs an end date.");
			return;
		}
		if (
			draft.productType === TikTokAdsProductType.TOPVIEW &&
			!draft.scheduleEnd
		) {
			setError("TopView (Reservation) requires a schedule end date.");
			return;
		}
		if (
			selectedGoal.requiresBid &&
			!(
				draft.bidPrice !== null &&
				draft.bidPrice !== undefined &&
				draft.bidPrice > 0
			)
		) {
			setError(
				`A ${selectedGoal.billing} bid greater than 0 is required (suggested ${selectedGoal.defaultBid}).`,
			);
			return;
		}
		setError("");
		nextStep();
	};

	return (
		<Card>
			<Card.Header>Ad group</Card.Header>
			<Card.Body>
				<Form.Group className="mb-3">
					<Form.Label>Ad group name</Form.Label>
					<Form.Control
						value={draft.adGroupName}
						onChange={(e) => updateDraft({ adGroupName: e.target.value })}
						placeholder={`${draft.name} — Ad group`}
					/>
				</Form.Group>

				<div className="row">
					<Form.Group className="mb-3 col-md-6">
						<Form.Label>Starts</Form.Label>
						<Form.Control
							type="datetime-local"
							value={scheduleToInputValue(draft.scheduleStart)}
							onChange={(e) =>
								updateDraft({
									scheduleStart: inputValueToSchedule(e.target.value),
								})
							}
						/>
					</Form.Group>
					<Form.Group className="mb-3 col-md-6">
						<Form.Label>
							Ends{" "}
							{draft.productType === TikTokAdsProductType.TOPVIEW ? (
								<span className="text-secondary">(required for TopView)</span>
							) : (
								draft.budgetMode === TikTokAdsBudgetMode.DAILY && (
									<span className="text-secondary">(optional)</span>
								)
							)}
						</Form.Label>
						<Form.Control
							type="datetime-local"
							value={scheduleToInputValue(draft.scheduleEnd ?? "")}
							onChange={(e) =>
								updateDraft({
									scheduleEnd: e.target.value
										? inputValueToSchedule(e.target.value)
										: null,
								})
							}
						/>
					</Form.Group>
				</div>

				<Form.Group className="mb-3">
					<Form.Label>Target locations</Form.Label>
					{regionsLoading ? (
						<div>
							<Spinner size="sm" /> Loading locations…
						</div>
					) : (
						<Select
							multiple
							value={draft.locationIds}
							onChange={(e) =>
								updateDraft({
									locationIds: Array.from(
										e.target.selectedOptions,
										(option) => option.value,
									),
								})
							}
							style={{ height: 160 }}
						>
							{(regions ?? []).map((region) => (
								<option key={region.id} value={region.id}>
									{region.name}
								</option>
							))}
						</Select>
					)}
					<Form.Text>
						Hold Ctrl (Cmd on Mac) to select more than one location.
					</Form.Text>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Age groups</Form.Label>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
						{TIKTOK_ADS_AGE_GROUPS.map((group) => (
							<Form.Check
								key={group}
								type="checkbox"
								id={`age-${group}`}
								label={AGE_LABELS[group]}
								checked={draft.ageGroups.includes(group)}
								onChange={() => toggleAgeGroup(group)}
							/>
						))}
					</div>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Gender</Form.Label>
					<Select
						value={draft.gender}
						onChange={(e) =>
							updateDraft({ gender: e.target.value as TikTokAdsGender })
						}
					>
						<option value={TikTokAdsGender.UNLIMITED}>All</option>
						<option value={TikTokAdsGender.FEMALE}>Women</option>
						<option value={TikTokAdsGender.MALE}>Men</option>
					</Select>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Placements</Form.Label>
					{draft.objective === TikTokAdsObjective.LEAD_GENERATION ? (
						<>
							<Form.Control value="TikTok only" disabled />
							<Form.Text>
								Lead Generation Instant Forms only support TikTok placement.
							</Form.Text>
						</>
					) : draft.objective === TikTokAdsObjective.VIDEO_VIEWS ? (
						<>
							<Form.Control value="Manual (required)" disabled />
							<div style={{ display: "flex", gap: 12, marginTop: 8 }}>
								{PLACEMENT_OPTIONS.map((option) => (
									<Form.Check
										key={option.value}
										type="checkbox"
										id={`placement-${option.value}`}
										label={option.label}
										checked={draft.placements.includes(option.value)}
										onChange={() => togglePlacement(option.value)}
									/>
								))}
							</div>
							<Form.Text>
								Video Views requires manual placement — automatic is rejected by
								TikTok.
							</Form.Text>
						</>
					) : (
						<>
							<Select
								value={draft.placementType}
								onChange={(e) =>
									updateDraft({
										placementType: e.target.value as TikTokAdsPlacementType,
									})
								}
							>
								<option value={TikTokAdsPlacementType.AUTOMATIC}>
									Automatic (recommended)
								</option>
								<option value={TikTokAdsPlacementType.MANUAL}>
									Choose placements
								</option>
							</Select>
							{draft.placementType === TikTokAdsPlacementType.MANUAL && (
								<div style={{ display: "flex", gap: 12, marginTop: 8 }}>
									{PLACEMENT_OPTIONS.map((option) => (
										<Form.Check
											key={option.value}
											type="checkbox"
											id={`placement-${option.value}`}
											label={option.label}
											checked={draft.placements.includes(option.value)}
											onChange={() => togglePlacement(option.value)}
										/>
									))}
								</div>
							)}
						</>
					)}
				</Form.Group>

				<div className="row">
					<Form.Group className="mb-3 col-md-6">
						<Form.Label>Optimization goal</Form.Label>
						<Select
							value={draft.optimizationGoal}
							onChange={(e) => {
								const goal = e.target.value as TikTokAdsOptimizationGoal;
								const option =
									findGoalOption(draft.objective, goal) ??
									getDefaultGoalForObjective(draft.objective);
								updateDraft({
									optimizationGoal: option.goal,
									billingEvent: option.billing,
									bidPrice: option.requiresBid ? option.defaultBid : null,
								});
							}}
						>
							{goalOptions.map((option) => (
								<option key={option.goal} value={option.goal}>
									{option.label}
								</option>
							))}
						</Select>
						<Form.Text>Locked to valid combos for {draft.objective}.</Form.Text>
					</Form.Group>

					<Form.Group className="mb-3 col-md-6">
						<Form.Label>Billing event</Form.Label>
						<Form.Control value={draft.billingEvent} disabled readOnly />
						<Form.Text>Set automatically from the optimization goal.</Form.Text>
					</Form.Group>
				</div>

				{selectedGoal.requiresBid && (
					<Form.Group className="mb-3">
						<Form.Label>Bid amount ({selectedGoal.billing})</Form.Label>
						<Form.Control
							type="number"
							min="0.01"
							step="0.01"
							value={draft.bidPrice ?? ""}
							onChange={(e) =>
								updateDraft({
									bidPrice: e.target.value ? Number(e.target.value) : null,
								})
							}
						/>
						<Form.Text>
							Required for {selectedGoal.billing}. Suggested{" "}
							{selectedGoal.defaultBid} in your account currency.
						</Form.Text>
					</Form.Group>
				)}

				{error && <p className="text-danger">{error}</p>}

				<div style={{ display: "flex", gap: 8 }}>
					<Button variant="secondary" onClick={prevStep}>
						Back
					</Button>
					<Button onClick={handleNext}>Continue</Button>
				</div>
			</Card.Body>
		</Card>
	);
};
